import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
import { api } from './api';
import type { StorageLocation } from './locations';

/**
 * Scan to screen.  design.md §11.6
 *
 * ── A WAREHOUSE SCANNER IS A KEYBOARD ──────────────────────────────────────
 *
 * There is no driver here, no pairing, no device to register and no API for the
 * scanner to call. A USB or Bluetooth barcode scanner is a **keyboard wedge**:
 * it types the decoded string as keystrokes and presses Enter. That is the whole
 * hardware story, and it is why this feature is a listener rather than a
 * protocol — the same reason the printer needed an agent and the scanner does
 * not. Whatever the operator already owns will work.
 *
 * ── TELLING A SCAN FROM TYPING ─────────────────────────────────────────────
 *
 * A wedge sends characters far faster than fingers can: whole strings land in
 * under a tenth of a second. So a burst of keystrokes closer together than
 * `HUMAN_GAP_MS`, ending in Enter, is a scan; anything slower resets the buffer
 * and is somebody typing.
 *
 * The threshold matters in one direction only. Too tight and a slow scanner is
 * ignored, which is visible immediately and annoying. Too loose and a fast
 * typist's keystrokes are swallowed and the page navigates away mid-sentence,
 * which is alarming and hard to explain. 35 ms is comfortably above any wedge
 * and comfortably below any person — 300 wpm is a keystroke every 40 ms.
 *
 * ── AND WHEN A FIELD ALREADY WANTS IT ──────────────────────────────────────
 *
 * If the focus is in an input, the keystrokes are that field's and this does
 * nothing. That is not politeness: the warehouse screen has a "SKU or barcode"
 * box built to be scanned into, and hijacking it would break the one place
 * scanning already worked.
 *
 * ── A PREFIX MAKES THE GUESS UNNECESSARY, AND IS NOT REQUIRED ──────────────
 *
 * Most scanners can be programmed to send a key before every code. Configure
 * one and the timing heuristic stops being load-bearing: a burst that starts
 * with the prefix IS a scan, whatever its timing, and it can be recognised even
 * while a field has the focus — so the two compromises above both lift.
 *
 * OPTIONAL, though, and that is the design. Requiring it would mean a new or
 * replacement scanner is silently dead until somebody finds the vendor's manual
 * and scans a configuration barcode out of it, and "I scanned it and nothing
 * happened" is a miserable support call. So an unprogrammed scanner keeps
 * working on timing, and a programmed one is believed.
 *
 * WHICH MEANS THE ARMED STATE MUST TIME OUT. A prefix arrives, no Enter
 * follows — the scanner misfired, or somebody pressed F9 — and without a
 * deadline this would swallow every keystroke afterwards. That failure is worse
 * than anything the heuristic can do, so the window is short and bounded.
 */

/** Closer together than this and it is a machine. See the header. */
const HUMAN_GAP_MS = 35;

/** Shorter than this and it is somebody leaning on Enter, not a code. */
const SHORTEST_CODE = 4;

/**
 * How long the listener stays armed with nothing arriving.
 *
 * AN IDLE WINDOW, NOT A TOTAL BUDGET, and the difference is the whole feature.
 * A budget from the prefix would put a ceiling on how long a code may take, so
 * the slow Bluetooth wedge this exists to rescue would be thrown away by a
 * different rule than the one it was rescued from. Measured from the last key
 * instead, a scan may take as long as it likes and a misfired prefix still
 * closes a second and a half later.
 */
const ARMED_IDLE_MS = 1500;

/**
 * Longer than any code worth printing, and a hard stop on the armed state.
 *
 * Belt and braces for the one case the idle window cannot close: a prefix that
 * fires by accident while somebody is typing steadily. 120 keystrokes is the
 * most that can be swallowed before it lets go.
 */
const LONGEST_CODE = 120;

/**
 * The key the scanner is programmed to send first, or '' for none.
 *
 * PER MACHINE, like the label's SRC field, because the scanner is plugged into
 * a desk rather than owned by a tenant — and because losing it degrades to the
 * timing heuristic rather than to nothing, which is the right way round for a
 * setting nobody will remember exists.
 */
const PREFIX_KEY = 'lasti.scanner.prefix';

export const scannerPrefix = ref<string>(localStorage.getItem(PREFIX_KEY) ?? '');

export function setScannerPrefix(key: string): void {
  scannerPrefix.value = key;
  if (key) localStorage.setItem(PREFIX_KEY, key);
  else localStorage.removeItem(PREFIX_KEY);
}

/**
 * F9, and the reasoning is worth keeping because it is all about what is
 * already taken: F1 is help, F3 find, F5 reload, F6 the address bar, F7 caret
 * browsing, F10 the menu bar, F11 fullscreen, F12 developer tools. F8 and F9
 * are what is left, and F9 is the one no debugger has an opinion about.
 *
 * A printable character would be a worse choice however convenient — `~` can be
 * typed, which gives back exactly the guarantee the prefix was bought for.
 */
export const SUGGESTED_PREFIX = 'F9';

export type ScanTarget =
  | { kind: 'location'; code: string; location: StorageLocation }
  | { kind: 'locations'; code: string; locations: StorageLocation[] }
  | { kind: 'product'; code: string; sku: string; name: string | null; orderNo?: string }
  | { kind: 'unknown'; code: string };

/**
 * `protocache/35286/TRA-GT2-BELT-6MM-852MM/2` — what a part label's Data Matrix
 * carries, composed server-side in `jobs.repository.ts`.
 *
 * Read here rather than sent to the server to interpret, because the shape is
 * the shop's own convention and the SKU is the only part this needs. The
 * segments are shop, load, part, count.
 */
export function partPayload(code: string): { sku: string; loadId: string } | null {
  const parts = code.split('/');
  if (parts.length !== 4) return null;
  const [, loadId, sku] = parts;
  return sku && sku.length > 0 ? { sku, loadId: loadId ?? '' } : null;
}

/**
 * What a scanned string points at.
 *
 * ORDERED BY HOW SURE WE ARE, not by how likely. A part label's payload is
 * unmistakable — four slash-separated segments is not a SKU and not an address
 * — so it is decided without asking the server anything. Everything else costs
 * one lookup, and a bin address is tried before a product because an address
 * that is also a SKU would otherwise send a picker to the wrong screen.
 */
export async function resolveScan(raw: string): Promise<ScanTarget> {
  const code = raw.trim();

  const part = partPayload(code);
  if (part) {
    return { kind: 'product', code, sku: part.sku, name: null, orderNo: part.loadId || undefined };
  }

  const at = await api.locationsAt(code).catch(() => ({ items: [] as StorageLocation[] }));
  if (at.items.length === 1) return { kind: 'location', code, location: at.items[0]! };
  if (at.items.length > 1) return { kind: 'locations', code, locations: at.items };

  // A SKU, a barcode, or a product name. `matchedField` says which, and one
  // exact hit is the only thing worth navigating on: two candidates is a search
  // result, and dropping somebody on an arbitrary one of them is worse than
  // telling them there were two.
  const found = await api
    .products({ q: code, limit: 2 })
    .catch(() => ({ items: [] as { sku: string; name: string | null }[] }));
  const items = found.items as { sku: string; name: string | null }[];
  if (items.length === 1) {
    return { kind: 'product', code, sku: items[0]!.sku, name: items[0]!.name };
  }

  return { kind: 'unknown', code };
}

/**
 * Listen for a scan anywhere on the page.
 *
 * Mounted once, in App.vue, so it works on every screen — a picker with a
 * scanner does not want to find the right page before scanning; finding the
 * right page is what they are scanning for.
 */
export function useScanner(onScan: (code: string) => void): { buffer: Ref<string> } {
  const buffer = ref('');
  let lastKeyAt = 0;
  /** Set by the prefix. While this is in the future, every key is the scanner's. */
  let armedUntil = 0;

  function fromAField(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el || !el.tagName) return false;
    return (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'SELECT' ||
      el.isContentEditable
    );
  }

  function onKeydown(event: KeyboardEvent): void {
    const now = event.timeStamp || performance.now();
    const prefix = scannerPrefix.value;

    // A shortcut is not a scan, and swallowing Ctrl-R would be memorable.
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // ---- the configured prefix -------------------------------------------
    if (prefix && event.key === prefix) {
      // Swallowed, or F9 reaches whatever else listens for it.
      event.preventDefault();
      armedUntil = now + ARMED_IDLE_MS;
      buffer.value = '';
      lastKeyAt = now;
      return;
    }

    const armed = armedUntil > now;
    if (armed) {
      // EVERY KEY IS THE SCANNER'S, including into a focused field — the point
      // of programming a prefix is that scanning works without first clicking
      // away from whatever somebody was typing in. `preventDefault` keeps the
      // code out of that field, so it is not both navigated and typed.
      event.preventDefault();
      if (event.key === 'Enter') {
        const code = buffer.value;
        buffer.value = '';
        armedUntil = 0;
        if (code.length > 0) onScan(code);
        return;
      }
      if (event.key.length === 1) {
        buffer.value += event.key;
        if (buffer.value.length >= LONGEST_CODE) {
          // Nothing sane is this long. Let go rather than keep eating.
          buffer.value = '';
          armedUntil = 0;
          return;
        }
      }
      // Each key pushes the window out, so a long code or a slow radio is fine
      // and only silence closes it.
      armedUntil = now + ARMED_IDLE_MS;
      lastKeyAt = now;
      return;
    }

    // ---- no prefix configured: the timing heuristic ------------------------
    if (fromAField(event.target)) return;

    const gap = now - lastKeyAt;
    lastKeyAt = now;

    if (event.key === 'Enter') {
      const code = buffer.value;
      buffer.value = '';
      // The gap before Enter matters as much as the gaps inside the code: a
      // buffer left over from a person typing, then Enter pressed a second
      // later, is not a scan.
      if (code.length >= SHORTEST_CODE && gap < HUMAN_GAP_MS) onScan(code);
      return;
    }

    // Printable characters only. A wedge sends no modifiers and no arrows.
    if (event.key.length !== 1) return;
    buffer.value = gap > HUMAN_GAP_MS ? event.key : buffer.value + event.key;
  }

  onMounted(() => document.addEventListener('keydown', onKeydown));
  onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

  return { buffer };
}
